// MUI components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";

// Project styles and helpers
import cancel_confirm_styles from "./cancel_confirm_styles";
import {
  adjustDateIfInTerm,
  format_date,
} from "../../../../../services/global.services";

// State management
import { useAppSelector } from "../../../../../services/StoreHelper";

interface CancelAgreementDialogProps {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  contract: any;
  setNote: (note: string) => void;
  cancelDate: any;
}

export default function CancelConfirm({
  open,
  onClose,
  onCancel,
  contract,
  setNote,
  cancelDate,
}: CancelAgreementDialogProps) {
  const { user, club } = useAppSelector((state) => state.app);

  // 🔹 simple toggle (same pattern as other files)
  const { usesSpanish } = club;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          fontFamily: "'Montserrat', sans-serif",
          borderRadius: 2,
          padding: 3,
          width: 400,
          "& .MuiInputBase-inputMultiline": {
            padding: "12px",
          },
          "&::placeholder": {
            paddingLeft: "4px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: 24,
          pb: 1,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        {usesSpanish ? "¿Cancelar acuerdo?" : "Cancel Agreement?"}
      </DialogTitle>

      <DialogContent dividers sx={{ fontFamily: "'Montserrat', sans-serif" }}>
        {club.onlineCancelMessage && (
          <Typography variant="body2" mb={2}>
            {club.onlineCancelMessage}
          </Typography>
        )}

        {contract.onlineCancelReview ? (
          <Typography variant="body2" mb={2}>
            {usesSpanish ? (
              <>
                Tu solicitud para cancelar{" "}
                <strong>{contract.name}</strong> será enviada para su
                procesamiento el{" "}
                <strong>{format_date(new Date())}</strong>. Tu acuerdo permanecerá
                activo hasta que la solicitud sea procesada.
              </>
            ) : (
              <>
                Your request to cancel <strong>{contract.name}</strong> will be
                submitted for processing on{" "}
                <strong>{format_date(new Date())}</strong>. Your agreement will
                remain active until the request is processed.
              </>
            )}
          </Typography>
        ) : (
          <div>
            {!contract.installmentsRemaining &&
              (!contract.noticePeriodDays ||
                parseInt(contract.noticePeriodDays) === 0) && (
                <Typography variant="body2" mb={2}>
                  {usesSpanish ? (
                    <>
                      Tu solicitud para cancelar{" "}
                      <strong>{contract.name}</strong> para{" "}
                      <strong>
                        {user.FirstName} {user.LastName}
                      </strong>{" "}
                      será efectiva el <strong>{cancelDate}</strong>.
                      {contract.cancellationFee && (
                        <>
                          {" "}
                          Se cobrará una tarifa de cancelación de{" "}
                          <strong style={{ color: "#F26444" }}>
                            ${contract.cancellationFee}
                          </strong>{" "}
                          a tu tarjeta registrada el{" "}
                          <strong>{format_date(new Date())}</strong>.
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      Your request to cancel your{" "}
                      <strong>{contract.name}</strong> for{" "}
                      <strong>
                        {user.FirstName} {user.LastName}
                      </strong>{" "}
                      will be effective on <strong>{cancelDate}</strong>.
                      {contract.cancellationFee && (
                        <>
                          {" "}
                          A cancellation fee of{" "}
                          <strong style={{ color: "#F26444" }}>
                            ${contract.cancellationFee}
                          </strong>{" "}
                          will be billed to your card on file on{" "}
                          <strong>{format_date(new Date())}</strong>.
                        </>
                      )}
                    </>
                  )}
                </Typography>
              )}

            {parseInt(contract.installmentsRemaining) > 0 && (
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {usesSpanish ? (
                  <>
                    Este acuerdo todavía está dentro del plazo. La fecha de
                    cancelación de <strong>{contract.name}</strong> será efectiva
                    el{" "}
                    <strong>
                      {adjustDateIfInTerm(
                        contract.lastInstallmentDueDateUtc,
                        contract.installmentsRemaining > 0
                      )}
                    </strong>
                    , un día después de la fecha de vencimiento de la última
                    factura (
                    <strong>
                      {adjustDateIfInTerm(contract.lastInstallmentDueDateUtc)}
                    </strong>
                    ). Eres responsable de pagar todas las facturas restantes.
                    El acuerdo no se renovará automáticamente y no se cobrará
                    una tarifa de cancelación.
                  </>
                ) : (
                  <>
                    This agreement is still in term status. The cancellation
                    date of your <strong>{contract.name}</strong> will be
                    effective on{" "}
                    <strong>
                      {adjustDateIfInTerm(
                        contract.lastInstallmentDueDateUtc,
                        contract.installmentsRemaining > 0
                      )}
                    </strong>
                    , one day after the final invoice due date of{" "}
                    <strong>
                      {adjustDateIfInTerm(contract.lastInstallmentDueDateUtc)}
                    </strong>
                    . You are responsible for paying all remaining invoices. The
                    agreement will not auto-renew and a cancellation fee will
                    not be charged.
                  </>
                )}
              </Typography>
            )}

            {!contract.installmentsRemaining &&
              contract.noticePeriodDays &&
              parseInt(contract.noticePeriodDays) > 0 && (
                <Typography variant="body2" mb={2} sx={{ whiteSpace: "pre-line" }}>
                  {usesSpanish ? (
                    <>
                      Este acuerdo requiere un aviso de cancelación de{" "}
                      <strong>{contract.noticePeriodDays}</strong> días. La
                      cancelación de <strong>{contract.name}</strong> será
                      efectiva en{" "}
                      <strong>{contract.noticePeriodDays} días</strong>, el{" "}
                      <strong>{format_date(cancelDate)}</strong>. Sigues siendo
                      responsable de cualquier pago debido dentro de los
                      próximos{" "}
                      <strong>{contract.noticePeriodDays}</strong> días. No se
                      realizarán pagos después de{" "}
                      <strong>{format_date(cancelDate)}</strong>.
                      {contract.cancellationFee && (
                        <>
                          {" "}
                          Se cobrará una tarifa de cancelación de{" "}
                          <strong style={{ color: "#F26444" }}>
                            ${contract.cancellationFee}
                          </strong>{" "}
                          a tu tarjeta registrada el{" "}
                          <strong>{format_date(new Date())}</strong>.
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      This agreement requires a cancellation notice of{" "}
                      <strong>{contract.noticePeriodDays}</strong> days. The
                      cancellation date of your{" "}
                      <strong>{contract.name}</strong> will be effective{" "}
                      <strong>{contract.noticePeriodDays} days</strong> from
                      today on <strong>{format_date(cancelDate)}</strong>. You
                      are still responsible for any payments due within the next{" "}
                      <strong>{contract.noticePeriodDays}</strong> days. No
                      payments will be due after{" "}
                      <strong>{format_date(cancelDate)}</strong>.
                      {contract.cancellationFee && (
                        <>
                          {" "}
                          A cancellation fee of{" "}
                          <strong style={{ color: "#F26444" }}>
                            ${contract.cancellationFee}
                          </strong>{" "}
                          will be billed to your card on file on{" "}
                          <strong>{format_date(new Date())}</strong>.
                        </>
                      )}
                    </>
                  )}
                </Typography>
              )}
          </div>
        )}

        <div style={cancel_confirm_styles.input}>
          <div style={cancel_confirm_styles.input_title_container}>
            <h5 style={cancel_confirm_styles.input_title}>
              {usesSpanish
                ? "Por favor dinos por qué estás cancelando este acuerdo"
                : "Please tell us why you are canceling this agreement"}
            </h5>
          </div>
          <div style={cancel_confirm_styles.field_and_helper_text}>
            <input
              style={cancel_confirm_styles.field}
              placeholder={
                usesSpanish ? "Agrega un motivo (opcional)" : "Add a reason (optional)"
              }
              onChange={(event: any) => setNote(event.target.value)}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "flex-end",
          pt: 2,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          sx={{ textTransform: "none" }}
        >
          {usesSpanish ? "Volver" : "Go Back"}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onCancel}
          sx={{
            textTransform: "none",
            background: "#F26444",
            color: "white",
          }}
        >
          {usesSpanish ? "Confirmar" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}